/**
 * CLE-2811 — the AI's reply while a pass runs: the last item of the conversation, written as it arrives.
 *
 * It shows the worker's streamed narration (`progress.narration`, remembered by `rememberNarration` so it
 * survives the worker clearing its progress at deposit) with a blinking cursor, formatted like any other
 * reply. A pass that streams no text — the fast facts pipeline reports steps instead — shows its activity
 * feed (`progress.activity`, remembered by `rememberActivity`), newest step last. Before either:
 * "Thinking…" and how long the pass has been going (`liveReplyContent` picks). When the pass ends, the
 * stored reply takes this bubble's place — same position, same styling.
 *
 * CLE-2811 (in place) — during a REVISION pass (a chat change on a run that already has a model) the page
 * shows no progress strip: this bubble is the progress. With no narration yet it shows one line — what the
 * pass is doing (`progressLine`: "Updating the Cigna PPO inpatient reimbursement…") beside the spinner — and,
 * for the whole pass, a small Stop link (`onStop`), the strip's Stop: it stops the pass at once (no dialog) and reads
 * "Stopping…", disabled, while that request is in flight.
 */

import { Button } from "primereact/button";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { DraftRunStatus } from "../../../store/queries/contractModelDrafts";
import {
  LIVE_REPLY_ARIA,
  LIVE_REPLY_THINKING,
  STOP_MODEL,
  STOP_MODEL_TOOLTIP,
  STOPPING,
} from "../copy";
import { blink } from "../progress/styles";
import { formatCounter, generationElapsedMs, isProcessing } from "../utils";
import ChatMarkdown from "./chatMarkdown";
import { liveReplyContent } from "./conversation";

const Cursor = styled.span`
  display: inline-block;
  width: 0.45em;
  height: 1em;
  margin-left: 0.15em;
  vertical-align: text-bottom;
  border-radius: 1px;
  background: var(--primary-color);
  animation: ${blink} 1.1s steps(1) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;

const Thinking = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-color-secondary);
  font-variant-numeric: tabular-nums;
`;

/** The pipeline's steps, one per line, muted like the "Thinking…" line they replace; the newest is last. */
const Activity = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--text-color-secondary);
  word-break: break-word;
  overflow-wrap: anywhere;

  li:last-child {
    color: var(--text-color);
  }
`;

/** The revision pass's Stop, as small as a link, under the live reply. */
const StopRow = styled.div`
  margin-top: 0.25rem;
`;

interface ILiveReplyProps {
  /** The narration so far; "" before the first words (or from a worker without narration). */
  text: string;
  /** CLE-2811 — the worker's last actions, shown when there is no narration (the fast facts pipeline). */
  activity?: string[];
  run: { status: DraftRunStatus; startedAt: string | null; updatedAt: string };
  /**
   * CLE-2811 (in place) — a revision pass's line (`revisionProgressLine`): shown instead of the activity feed
   * and "Thinking…" while there is no narration. Absent outside a revision pass.
   */
  progressLine?: string | null;
  /** CLE-2811 (in place) — a revision pass's Stop (the page's stop — the pass only, no dialog). Absent: no Stop link. */
  onStop?: () => void;
  stopping?: boolean;
}

function LiveReply({
  text,
  activity,
  run,
  progressLine = null,
  onStop,
  stopping = false,
}: ILiveReplyProps) {
  const content = liveReplyContent(text, activity);
  const showLine = !!progressLine && content.kind !== "narration";
  const thinking = content.kind === "thinking" && !showLine;
  // Only the "Thinking… 12 s" counter needs a clock; the text arrives with the page's poll.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!thinking) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [thinking]);

  const stop = onStop ? (
    <StopRow>
      <Button
        type="button"
        label={stopping ? STOPPING : STOP_MODEL}
        className="p-button-link p-button-sm p-0"
        tooltip={STOP_MODEL_TOOLTIP}
        tooltipOptions={{ position: "bottom" }}
        disabled={stopping}
        onClick={onStop}
      />
    </StopRow>
  ) : null;

  if (content.kind === "narration") {
    return (
      <div aria-label={LIVE_REPLY_ARIA} aria-busy="true">
        <ChatMarkdown
          text={content.text.trimEnd()}
          trailing={<Cursor aria-hidden />}
        />
        {stop}
      </div>
    );
  }
  if (showLine) {
    return (
      <div>
        <Thinking aria-label={LIVE_REPLY_ARIA} aria-busy="true">
          <i
            className="pi pi-spin pi-spinner"
            style={{ fontSize: "0.85rem" }}
          />
          <span>{progressLine}</span>
        </Thinking>
        {stop}
      </div>
    );
  }
  if (content.kind === "activity") {
    return (
      <Activity aria-label={LIVE_REPLY_ARIA} aria-busy="true">
        {content.lines.map((line, i) => (
          // Lines repeat ("Reading page 3…" twice) and the list slides, so the index is the identity.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            {line}
            {i === content.lines.length - 1 ? <Cursor aria-hidden /> : null}
          </li>
        ))}
      </Activity>
    );
  }
  // Between a Regenerate click and the next poll the run still reads as finished; its clock would be the
  // previous pass's, so say nothing rather than something wrong.
  const elapsed = isProcessing(run.status)
    ? generationElapsedMs(run, now)
    : null;
  return (
    <Thinking aria-label={LIVE_REPLY_ARIA} aria-busy="true">
      <i className="pi pi-spin pi-spinner" style={{ fontSize: "0.85rem" }} />
      {LIVE_REPLY_THINKING}
      {elapsed != null ? <span>{formatCounter(elapsed)}</span> : null}
    </Thinking>
  );
}

export default LiveReply;
